/**
 * /tenants/$slug/applications/$appId — detail route shim.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { fetchApplication, fetchApplicationWorkspaceCapabilities } from "@/features/applications/queries";
import { queryKeys } from "@/lib/query-keys";
import { ApplicationDetailPage } from "@/features/applications/pages/application-detail-page";
import { applicationWorkspaceSearchSchema } from "@/features/applications/application-workspace";

export const Route = createFileRoute("/_authed/tenants/$slug/applications/$appId")({
  validateSearch: (raw: Record<string, unknown>) =>
    applicationWorkspaceSearchSchema.parse(raw),
  beforeLoad: ({ context, params, search }) => {
    if (search.section !== "access-requests" && search.section !== "assignments") return;
    const permissions = context.authority.permissions;
    const canView = permissions.some(
      (permission) => permission === "users.read" || permission === "users.read!",
    );
    if (!canView) {
      throw redirect({
        to: "/tenants/$slug/applications/$appId",
        params: { slug: params.slug, appId: params.appId },
        search: {},
        replace: true,
      });
    }
  },
  loader: async ({ context, params, location }) => {
    const app = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.applications.detail(context.tenant.id, params.appId),
      queryFn: () => fetchApplication(context.tenant.id, params.appId),
    });
    const permissions = context.authority.permissions;
    const hasProductRead = permissions.some((permission) =>
      permission === "product_areas.read" || permission === "product_areas.read!"
    );
    const capabilities = hasProductRead
      ? await context.queryClient.ensureQueryData({
          queryKey: queryKeys.productAreas.applicationWorkspace(context.tenant.id, params.appId),
          queryFn: () => fetchApplicationWorkspaceCapabilities(context.tenant.id, params.appId),
        }).catch(() => null)
      : null;
    const requested = applicationWorkspaceSearchSchema.parse(
      Object.fromEntries(new URLSearchParams(location.search)),
    ).section;
    const decision = capabilities?.data.find((item) =>
      item.capability === (requested === "access-requests" ? "request_policy" : requested),
    );
    if (requested === "access-requests" && (!decision || decision.availability === "hidden")) {
      throw redirect({ to: "/tenants/$slug/applications/$appId", params, search: {}, replace: true });
    }
    if (requested === "assignments" && decision?.availability === "hidden") {
      throw redirect({ to: "/tenants/$slug/applications/$appId", params, search: {}, replace: true });
    }
    return app;
  },
  component: ApplicationDetailPage,
});
